import { createReducerFunction, ImmerReducer } from 'immer-reducer';

export type ExpenseOrigin = 'admin' | 'employee';
export type ExpenseState = 'active' | 'deferred' | 'excluded';

// Hryvnia expenses stay in hryvnia; every sum comes back per currency.
export type Currency = 'USD' | 'UAH';

export interface PayoutLine {
  // 'e:<id>' for a stored row, 'r:<recurringId>' for a recurring occurrence
  // that no admin has acted on yet, so has no row of its own.
  ref: string;
  expenseId?: number;
  recurringId?: number;
  title: string;
  amount: number;
  // Missing on a copy stored before currencies existed: read it as USD.
  currency?: Currency;
  isRecurring: boolean;
  origin: ExpenseOrigin;
  state: ExpenseState;
  deferredTo?: string;
  deferredFrom?: string;
  canDelete: boolean;
  // Proof offered for the line. Always empty on a recurring line nobody has
  // acted on yet; attaching gives it a row of its own for that month.
  attachments: ExpenseAttachment[];
}

export interface ExpenseAttachment {
  _id: number;
  expenseId: number;
  fileName: string;
  mimeType: string;
  size: number;
  uploadedBy?: number;
  createdAt: number;
  // Whoever uploaded it, or an admin.
  canDelete: boolean;
}

export interface Payout {
  period: string;
  lines: PayoutLine[];
  // Dollars only; hryvnia lines are summed apart.
  expensesTotal: number;
  expensesTotalUah?: number;
  // Admin-only. Absent from the employee response entirely -- not blanked out,
  // never sent.
  salary?: number;
  total?: number;
  user?: { _id: number; name: string; photo?: string; smallPhoto?: string; role: string };
}

export interface SalaryRecord {
  _id: number;
  userId: number;
  amount: number;
  effectiveFrom: string;
  comment?: string;
  addedBy?: number;
  createdAt: number;
  change?: number;
}

export interface PayoutHistoryRecord {
  _id: number;
  userId: number;
  period: string;
  amount: number;
  note?: string;
  source: string;
}

export interface SalaryOverview {
  userId: number;
  current?: number;
  history: SalaryRecord[];
  payoutHistory: PayoutHistoryRecord[];
}

export interface ExpenseSuggestion {
  title: string;
  lastAmount: number;
  lastCurrency?: Currency;
  lastUsedAt: number;
  timesUsed: number;
}

// A cell is what was actually paid ('recorded', straight from the accounting
// history and immutable), what today's salary and expenses come to
// ('projected', and it moves if either is edited), or nothing at all.
export type PayoutCellSource = 'recorded' | 'projected' | 'none';

export interface PayoutCell {
  period: string;
  amount?: number;
  // Hryvnia owed on top of `amount`, never folded into it.
  uah?: number;
  source: PayoutCellSource;
  note?: string;
}

// A payee is either a CRM user ('u:<id>') or a contractor ('c:<id>') -- someone
// the company pays who has no account.
export interface PayoutGridRow {
  ref: string;
  userId?: number;
  contractorId?: number;
  name: string;
  photo?: string;
  isArchived: boolean;
  isContractor: boolean;
  // No salary, nothing ever paid, nothing to reimburse -- typically an admin
  // account that exists to use the CRM rather than to be paid by it.
  isUnpaid: boolean;
  cells: PayoutCell[];
  total: number;
  totalUah?: number;
}

export interface PayoutGrid {
  periods: string[];
  rows: PayoutGridRow[];
  totals: { [period: string]: number };
  totalsUah?: { [period: string]: number };
  grandTotal: number;
  grandTotalUah?: number;
  earliestRecorded: string;
  showAll: boolean;
}

export interface Contractor {
  _id: number;
  name: string;
  note?: string;
  createdAt: number;
}

// Where a payee's money goes: the four fields HR collects for an IBAN transfer.
// Admin-only -- the server refuses every requisites route to anyone else, an
// employee's own included.
export interface PaymentRequisite {
  _id: number;
  userId?: number;
  contractorId?: number;
  // The account holder as the bank has it, which is not always the CRM name.
  name: string;
  iban: string;
  swift?: string;
  country?: string;
  addedBy?: number;
  createdAt: number;
  updatedAt: number;
}

// Tagged with the payee it belongs to ('u:<id>' / 'c:<id>', the grid's row ref),
// so a sheet opened for one person never renders what was left over from the
// previous one. `requisite` is null when nothing is on file, which is an
// ordinary state and not an error.
export interface PayeeRequisites {
  payee: string;
  requisite: PaymentRequisite | null;
}

export interface PayoutState {
  payout: Payout | null;
  salary: SalaryOverview | null;
  suggestions: ExpenseSuggestion[] | null;
  grid: PayoutGrid | null;
  requisites: PayeeRequisites | null;
}

const initialState: PayoutState = {
  payout: null,
  salary: null,
  suggestions: null,
  grid: null,
  requisites: null,
};

export class PayoutReducer extends ImmerReducer<PayoutState> {
  setPayout(payout: Payout) {
    this.draftState.payout = payout;
  }

  setSalary(salary: SalaryOverview) {
    this.draftState.salary = salary;
  }

  setSuggestions(suggestions: ExpenseSuggestion[]) {
    this.draftState.suggestions = suggestions;
  }

  setGrid(grid: PayoutGrid) {
    this.draftState.grid = grid;
  }

  setRequisites(requisites: PayeeRequisites) {
    this.draftState.requisites = requisites;
  }

  cleanPayout() {
    this.draftState = initialState;
  }
}

export default createReducerFunction(PayoutReducer, initialState);
